/**
 * A report document: what is saved, and what is read back.
 *
 * The document is stored as JSON, so this module is the boundary between a
 * typed tree the application can rely on and an untyped blob that came off a
 * server. Everything crossing that boundary is checked here, once.
 *
 * **Reading is deliberately forgiving.** A document may have been written by
 * an earlier version, by an import, or by a version that knew a section kind
 * this one does not. Refusing the whole report over one unrecognised section
 * would lose somebody's work to protect a type. So an unreadable section is
 * dropped and the rest of the report opens, which is the outcome that leaves
 * the most work intact.
 *
 * **Writing is exact.** What goes up is what the types describe.
 */

import { headingNumber, type HeadingNumber } from '@/domain/layout/dossier'
import { readDirections } from '@/domain/direction'
import { readTag } from '@/domain/esrs'
import { isJsonObject, readArray, readObject, readString } from '@/domain/json'
import type { JsonObject, JsonValue } from '@/domain/json'
import { DEFAULT_LAYOUT, readLayout, type Layout } from '@/domain/layout'
import { DEFAULT_TYPEFACE, isTypefaceId, type TypefaceId } from '@/domain/typeface'
import { newId, type Section } from '@/domain/section'
import { isSectionKind, readSectionOfKind } from '@/sections'
import {
  readDocumentReportings,
  writeDocumentReportings,
  type DocumentReporting,
} from '@/domain/csrdReportings'
import type { ImportedFont } from '@/domain/facsimile/fonts'
import { readImportedFonts } from '@/domain/facsimile/read'

/** A whole report. */
export interface ReportDocument {
  readonly sections: readonly Section[]
  /**
   * Where the document came from, when it came from somewhere. Kept so an
   * import can be explained months later.
   */
  readonly origin: string
  /**
   * What the report is set in.
   *
   * Stored with the document rather than remembered per reader, so everyone
   * sees the same report and the PDF matches the screen.
   */
  readonly typeface: TypefaceId
  /** The paper, the margins, the text size, and whether it is justified. */
  readonly layout: Layout
  /**
   * The fonts pages imported from a PDF are drawn in.
   *
   * With the document rather than with each page, because one font serves
   * every page that uses it, and replacing it once (an upload, a substitute)
   * has to reach them all.
   */
  readonly fonts: readonly ImportedFont[]
  /**
   * The reportings of CSRD this report is tied to: one principal, which the
   * report is about, and any secondary ones it also takes figures from. Empty
   * for a report made before they existed, which is read from whichever
   * reporting CSRD is on, as it always was.
   */
  readonly reportings: readonly DocumentReporting[]
}

export function emptyDocument(): ReportDocument {
  return {
    sections: [],
    origin: '',
    typeface: DEFAULT_TYPEFACE,
    layout: DEFAULT_LAYOUT,
    fonts: [],
    reportings: [],
  }
}

/**
 * One section, or null when it cannot be read.
 *
 * Null rather than a throw: a single unreadable section should cost that
 * section, not the document. A kind this version does not know is exactly that
 * case, and it happens whenever a report written by a later version is opened
 * by an earlier one.
 *
 * The reading itself belongs to the kind, in `sections/<kind>/`.
 */
export function readSection(value: JsonValue): Section | null {
  if (!isJsonObject(value)) return null

  const kind = readString(value, 'kind')
  if (!isSectionKind(kind)) return null

  const section = readSectionOfKind(kind, {
    id: readString(value, 'id') || newId(),
    // Left as it was stored, blank included. The outline shows the kind's own
    // name for an untitled section, in the reader's language, which is better
    // than a fallback frozen into the document when it was written.
    title: readString(value, 'title'),
    stored: value,
    readChildren: readSections,
  })

  // The ESRS tag is read here rather than by each of the fifteen kinds. It
  // belongs to the section, not to what the section is, and reading it in one
  // place is what stops a kind added later from quietly not supporting it.
  const stored = readObject(value, 'tag')
  const tag = readTag(stored['ref'], stored['materiality'])
  const tagged = tag ? { ...section, tag } : section

  // The directions a section is given to are read here too, for the same reason:
  // they belong to the section, not to what it is. Left off when there are none,
  // so a section nobody gave to a direction saves with no field.
  const directions = readDirections(value['directions'])
  const given = directions.length > 0 ? { ...tagged, directions } : tagged

  // A shell the server sent for a section this person may not read keeps its
  // mark, so it is drawn as a block and handed back as the shell it is.
  return value['masked'] === true ? { ...given, masked: true as const } : given
}

/** Every section that could be read, in order. */
export function readSections(values: readonly JsonValue[]): Section[] {
  const sections: Section[] = []
  for (const value of values) {
    const section = readSection(value)
    if (section) sections.push(section)
  }
  return sections
}

/** A document read out of a stored payload. */
export function readDocument(payload: JsonObject | null | undefined): ReportDocument {
  if (!payload) return emptyDocument()

  // A report written before this setting existed, or naming a typeface this
  // version does not know, opens in the default rather than not at all.
  const stored = readString(payload, 'typeface')

  return {
    sections: readSections(readArray(payload, 'sections')),
    origin: readString(payload, 'origin'),
    typeface: isTypefaceId(stored) ? stored : DEFAULT_TYPEFACE,
    layout: readLayout(readObject(payload, 'layout')),
    fonts: readImportedFonts(readArray(payload, 'fonts')),
    reportings: readDocumentReportings(readArray(payload, 'csrdReportings')),
  }
}

/** A document as it is stored. */
export function writeDocument(document: ReportDocument): JsonObject {
  return {
    sections: document.sections.map(writeSection),
    origin: document.origin,
    typeface: document.typeface,
    layout: {
      ...document.layout,
      palette: [...document.layout.palette],
      textStyles: document.layout.textStyles.map((style) => ({ ...style })),
    },
    fonts: document.fonts.map((font) => ({ ...font })),
    csrdReportings: writeDocumentReportings(document.reportings),
  }
}

function writeSection(section: Section): JsonObject {
  // Spread, then fix up the parts that are not plain JSON. Listing every field
  // per kind would be a second place to update whenever one is added, and the
  // one that gets forgotten.
  const base = { ...section } as unknown as JsonObject

  if (section.kind === 'group') {
    base['children'] = section.children.map(writeSection)
  }

  return base
}

/**
 * Every heading's number, by section id.
 *
 * One walk of the document rather than a calculation each heading does for
 * itself, because a heading's number depends entirely on what came before it
 * and nothing else in the tree knows that. Groups whose title is printed count
 * as headings, for the same reason they do in the contents: to a reader they
 * are chapters.
 *
 * A document that jumps a level, from a chapter straight to a sub-subsection,
 * would otherwise number its first one `1.0.1`. The skipped level counts as
 * its first instead, which is what a reader expects and what every word
 * processor quietly does.
 */
export function numberingOf(
  sections: readonly Section[],
  style: HeadingNumber,
): ReadonlyMap<string, string> {
  const numbers = new Map<string, string>()
  if (style === 'none') return numbers

  const counts: number[] = []

  /**
   * Count one heading at a level, and clear everything under it.
   *
   * The array is grown by pushing rather than by setting its length. Setting
   * the length leaves holes, and `map` skips holes rather than visiting them,
   * which produced numbers with gaps in the middle: `1..1`.
   */
  function bump(level: number): readonly number[] {
    while (counts.length < level) counts.push(0)
    for (let deeper = level; deeper < counts.length; deeper += 1) counts[deeper] = 0
    counts[level - 1] = (counts[level - 1] ?? 0) + 1
    return counts.slice(0, level).map((count) => Math.max(1, count))
  }

  function walk(list: readonly Section[], level: number): void {
    for (const section of list) {
      if (section.kind === 'group') {
        if (section.showTitle) numbers.set(section.id, headingNumber(style, bump(level)))
        walk(section.children, level + 1)
        continue
      }

      if (section.kind === 'heading') {
        numbers.set(section.id, headingNumber(style, bump(section.level)))
      }
    }
  }

  walk(sections, 1)
  return numbers
}

/**
 * The deepest a heading can go.
 *
 * A printed contents page is set shallower on purpose, because one that runs
 * for pages helps nobody. Anything navigating rather than printing wants all
 * of it: there is no reason to hide the heading somebody is hunting for.
 */
export const FULL_CONTENTS_DEPTH = 3

/** One line of the contents page. */
export interface ContentsEntry {
  readonly id: string
  readonly title: string
  readonly level: number
}

/**
 * The contents page, derived from the document each time it is asked for.
 *
 * Never stored. A contents page saved alongside the document is a contents
 * page that goes quietly wrong the first time somebody renames a chapter, and
 * nobody notices until it is printed.
 *
 * Groups count as a level, and a group set not to print its title still
 * contributes its depth to what it contains, because it is still a chapter as
 * far as the reader is concerned.
 */
export function contentsOf(
  sections: readonly Section[],
  depth: number,
  level = 1,
): ContentsEntry[] {
  const entries: ContentsEntry[] = []

  for (const section of sections) {
    if (section.kind === 'group') {
      if (section.showTitle && level <= depth) {
        entries.push({ id: section.id, title: section.title, level })
      }
      entries.push(...contentsOf(section.children, depth, level + 1))
      continue
    }

    if (section.kind === 'heading' && section.level <= depth) {
      entries.push({ id: section.id, title: section.title, level: section.level })
    }
  }

  return entries
}
